import React, { useState, useEffect, useMemo } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { toast } from 'react-toastify';
import qs from 'query-string';
import styled from 'styled-components';
import _ from 'lodash';

import * as Styles from './styles';
import { gateway } from '../../../../utils/api';
import { toastErrorAndWarningOptions } from '../../../../utils/functions';
import history from '../../../../utils/history';
import Loading from '../../../Loading/Loading';
import PlanCard from '../PlanCard';
import PromoPlanCard from '../PromoPlanCard'
import Warning, { WarningText } from '../Warning';

import { LitButton } from '../../../shared/LitButton';

const buttonStyles = {
  'font-size': '1rem',
  'font-weight': 'bold',
  'font-family': 'Lato',
  'margin-top': 'auto'
};

const IMG_LIT_SAINTPAUL = require('../../../../images/sp_lit.png');

const MobileHeader = styled.nav`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    width: 100%;
    background: #27aae1; /* Cor padrão do LIT */
    
    .navbar-brand {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      
      img {
        max-width: 120px;
        height: auto;
        max-height: 50px;
        object-fit: contain;
      }
    }
  }
`;

const LogoutButton = styled.button`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    width: calc(100% - 2rem);
    max-width: 500px;
    padding: 1rem;
    margin: 2rem auto;
    background: transparent;
    border: 2px solid #27aae1;
    color: #27aae1;
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.3s ease;
    
    &:hover {
      background: #27aae1;
      color: #fff;
    }
    
    &:active {
      opacity: 0.8;
    }
  }
`;

function PlanList({
  numberOfCards = 4,
  slidesToScroll = 1,
  showHeader,
  onSelectPlan
}) {
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [hasOpenInvoice, setHasOpenInvoice] = useState(false);
  const certificates = localStorage.getItem('certificates');
  const [
    hasExpiredOrNearExpirationInvoice,
    setHasExpiredOrNearExpirationInvoice
  ] = useState(false);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const renderPlansCards = useMemo(() => {
    const plansToRender = [];

    plans.forEach(plan => {
      plansToRender.push(plan);

      if (plan && plan.campaigns && plan.campaigns.length > 0) {
        plan.campaigns.forEach(campaing => {
          if (campaing && campaing.showNewCard) {
            plansToRender.push(campaing);
          }
          if (campaing && campaing.hideOriginalCard) {
            const index = plansToRender.indexOf(plan);

            plansToRender.splice(index, 1);
          }
        });
      }
    });

    return plansToRender;
  }, [plans]);

  function redirectToHistoryPayment() {
    history.push('invoicePaymentView', {
      nextPageParams: {
        fromPayment: true
      }
    });
  }

  function logout() {
    const loginTenantRaw = localStorage.getItem('loginTenant');
    const originRaw = localStorage.getItem('urlOriginParam');

    const loginTenant =
      loginTenantRaw && loginTenantRaw !== 'undefined'
        ? loginTenantRaw.toLowerCase()
        : '';
    const origin =
      originRaw && originRaw !== 'undefined' ? originRaw : '';

    let redirectLoginPath = '/';

    if (loginTenant) {
      redirectLoginPath = `/${loginTenant}`;
    }

    if (origin) {
      const separator = redirectLoginPath.includes('?') ? '&' : '?';
      redirectLoginPath = `${redirectLoginPath}${separator}o=${encodeURIComponent(
        origin
      )}`;
    }

    history.push({
      pathname: '/logout',
      state: {
        redirectLoginPath
      }
    });
  }

  function redirect() {
    history.push('/certificados');
  }

  useEffect(() => {
    async function loadPlans() {
      try {
        const urlOriginParam = localStorage.getItem('urlOriginParam');
        const loginTenant = localStorage.getItem('loginTenant');

        const queryParams = {};

        if (loginTenant) {
          queryParams.loginTenant = loginTenant;
        }

        if (urlOriginParam) {
          queryParams.origin = urlOriginParam;
        }

        const response = await gateway.get(
          `/webbff/lit_app_web/payment/plan?${qs.stringify(queryParams)}`
        );

        const { plans = [] } = response;
        setPlans(plans);

        if (plans.length === 1 && plans[0].alreadyPurchased === false) {
          onSelectPlan(plans[0]);
        }

        const pixDataStr = localStorage.getItem('pixData');

        if (pixDataStr) {
          const pixData = JSON.parse(pixDataStr);

          const pixOffer = response.plans.find(p => p.code === pixData.offerId);

          if (pixOffer) {
            onSelectPlan(pixOffer);
          }
        }
      } catch (error) {
        toast.error(
          'Ops, ocorreu um erro e não possível buscar os planos',
          toastErrorAndWarningOptions
        );
      } finally {
        setLoadingPlans(false);
      }
    }

    loadPlans();
  }, []);

  useEffect(() => {
    async function getOpenInvoices() {
      try {
        const response = await gateway.get(
          `/webbff/lit_app_web/payment/invoice/isOpenInvoices`
        );
        setHasOpenInvoice(response);
      } catch (error) {
        console.log(error);
      }
    }

    getOpenInvoices();
  }, []);

  useEffect(() => {
    async function consultUserInvoiceHistory() {
      try {
        const response = await gateway(
          '/webbff/lit_app_web/payment/invoice/history?detailedHistory=false'
        );

        setHasExpiredOrNearExpirationInvoice(
          response.hasExpiredOrNearExpirationInvoice
        );
      } catch (error) {
        console.log(error);
      }
    }

    consultUserInvoiceHistory();
  }, []);

  useEffect(() => {
    // Verificar se é mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Buscar configurações do tenant
    const getTenantSettings = () => {
      // Primeiro tenta pegar do domainSettings (usado no cadastro)
      const domainSettings = localStorage.getItem('domainSettings');
      if (domainSettings) {
        try {
          const parsed = JSON.parse(domainSettings);
          setTenantSettings(parsed);
          return;
        } catch (e) {
          console.error('Error parsing domainSettings', e);
        }
      }
      
      // Se não encontrar, tenta pegar do settings (usado no NavBarSuperior)
      const settings = localStorage.getItem('settings');
      const tenant = localStorage.getItem('loginTenant') || localStorage.getItem('tenant');
      
      if (settings && tenant) {
        try {
          const parsed = JSON.parse(settings);
          const tenantConfig = parsed[tenant];
          if (tenantConfig) {
            setTenantSettings(tenantConfig);
            return;
          }
        } catch (e) {
          console.error('Error parsing settings', e);
        }
      }
      
      // Se não encontrar nada, deixa null para usar padrão
      setTenantSettings(null);
    };
    
    getTenantSettings();
  }, []);

  // Obter logo do tenant ou usar padrão (mesma logo da tela de planos)
  const getLogo = () => {
    if (tenantSettings && tenantSettings.logoUrl) {
      return tenantSettings.logoUrl;
    }
    return IMG_LIT_SAINTPAUL;
  };

  // Obter cor de background do tenant ou usar padrão
  const getBackgroundColor = () => {
    if (tenantSettings) {
      return tenantSettings.headerBackgroundColor || 
             tenantSettings.primaryBackgroundColor || 
             '#27aae1';
    }
    return '#27aae1'; // Cor padrão do LIT
  };

  return (
    <Styles.Container>
      {isMobile && showHeader && (
        <MobileHeader 
          className="navbar navbar-lit navbar-expand-lg p-2"
          style={{ background: getBackgroundColor() }}
        >
          <a className="navbar-brand">
            <img src={getLogo()} alt="Logo" />
          </a>
        </MobileHeader>
      )}
      {showHeader && !isMobile && (
        <Styles.NavLit className="navbar navbar-lit navbar-expand-lg bg-gradient-lit">
          <a className="navbar-brand">
            <img src={IMG_LIT_SAINTPAUL} alt="logo_lit" />
          </a>
          <button type="button" onClick={() => logout()}>
            SAIR
          </button>
        </Styles.NavLit>
      )}
      <Styles.Content>
        {(hasOpenInvoice || hasExpiredOrNearExpirationInvoice) && (
          <Warning onClick={redirectToHistoryPayment}>
            <WarningText size={16}>
              Identificamos que você possuí boletos em aberto, vencidos ou
              próximo do vencimento. Caso já tenha executado o pagamento esse
              aviso pode ser ignorado, se deseja visualizar os boletos em aberto{' '}
              <WarningText bold>clique aqui</WarningText>.
            </WarningText>
          </Warning>
        )}
        {loadingPlans && <Loading width="30" />}
        {!loadingPlans && (
          <Styles.Cards itemsQuantity={plans.length} centerMode={plans.length <= 3}>
            <Styles.ContainerSlider>
              <Styles.Title>Escolha a melhor opção para você.</Styles.Title>

              <Styles.CustomItemsCarousel
                numberOfPlaceholderItems={1}
                numberOfCards={window.screen.width <= 600 ? 1 : numberOfCards}
                slidesToScroll={slidesToScroll}
                showSlither
                freeScrolling={false}
                chevronWidth={40}
                outsideChevron
                rightChevron={<MdChevronRight size={70} />}
                leftChevron={<MdChevronLeft size={70} />}
                requestToChangeActive={newIndex => {
                  setActiveCarouselIndex(newIndex);
                }}
                activeItemIndex={activeCarouselIndex}
                closeModal
              >
                {renderPlansCards.map((plan) => plan.promo ? 
                <PromoPlanCard key={plan.code} plan={plan} onSelectPlan={onSelectPlan} promo hasOpenInvoice={hasOpenInvoice} /> 
                  : <PlanCard key={plan.code} plan={plan} onSelectPlan={onSelectPlan} hasOpenInvoice={hasOpenInvoice} />)}
              </Styles.CustomItemsCarousel>
            </Styles.ContainerSlider>
            {!loadingPlans && certificates && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <LitButton
                  style={{
                    ...buttonStyles,
                    padding: '5px 20px',
                    'font-size': '16px',
                    'margin-bottom': '10px'
                  }}
                  onClick={redirect}
                >
                  VEJA SEUS CERTIFICADOS DISPONÍVEIS
                </LitButton>
              </div>
            )}
          </Styles.Cards>
        )}
        {isMobile && showHeader && (
          <LogoutButton onClick={logout}>
            SAIR
          </LogoutButton>
        )}
      </Styles.Content>
    </Styles.Container>
  );
}

export default PlanList;
